import React, { useState, useEffect, useRef } from "react";
import {
  fetchFeeTypes,
  createFeeType,
  updateFeeType,
  deleteFeeType,
} from "../../api/adminMasterApi";
import { getCsrfToken } from "../../../../public/api/authApi";
import {
  GlobalButton,
  GlobalTextField,
  GlobalSelectField,
} from "../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import { GlobalModal } from "../../../../global/GlobalModal";
import GlobalDataTable from "../../../../global/GlobalDataTable";
import GlobalBadge from "../../../../global/GlobalBadge";
import GlobalSuccessFlashModal from "../../../../global/GlobalSuccessFlashModal";

const STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 2, label: "Inactive" },
];

const FeeTypeMaster = ({ onDirectoryChange, canCreate = false, canEdit = false, canDelete = false, }) => {
  const [feeTypes, setFeeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    fee_type_name: "",
    description: "",
    status_id: 1,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formErrorDetails, setFormErrorDetails] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const alertRef = useRef(null);
  const formSectionRef = useRef(null);
  const [successMsg, setSuccessMsg] = useState("");

  const scrollToMasterForm = () => {
    requestAnimationFrame(() => {
      formSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  };

  useEffect(() => {
    loadFeeTypes();
  }, []);

  const loadFeeTypes = async () => {
    try {
      setLoading(true);
      const response = await fetchFeeTypes();
      if (response && response.success) {
        setFeeTypes(response.data || []);
      } else {
        setFeeTypes(response || []);
      }
    } catch (err) {
      setError(err.message || "Failed to load fee types");
    } finally {
      setLoading(false);
    }
  };

  const setFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrorDetails[name]) {
      setFormErrorDetails((prev) => ({ ...prev, [name]: "" }));
    }
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isEditing ? !canEdit : !canCreate) return;
    setFormError(null);
    setFormErrorDetails({});
    setSuccessMsg("");
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      let result;
      if (isEditing) {
        result = await updateFeeType(
          formData.id,
          {
            fee_type_name: formData.fee_type_name.trim(),
            description: formData.description,
            status_id: Number(formData.status_id),
          },
          csrfToken,
        );
      } else {
        result = await createFeeType(
          {
            fee_type_name: formData.fee_type_name,
            description: formData.description,
          },
          csrfToken,
        );
      }
      if (result?.error) {
        setFormError(result.error.message || "Failed to save fee type");
        setFormErrorDetails(result.error.details || {});
        setTimeout(() => alertRef.current?.focus(), 0);
        return;
      }
      if (result?.reactivated) {
        setSuccessMsg("Fee type restored to the directory.");
      } else {
        setSuccessMsg(
          isEditing
            ? "Fee type updated successfully."
            : "Fee type created successfully.",
        );
      }
      handleCancel();
      await loadFeeTypes();
      onDirectoryChange?.();
    } catch (err) {
      setFormError(err.message);
      setTimeout(() => alertRef.current?.focus(), 0);
    }
  };

  const handleEdit = (feeType) => {
    setSuccessMsg("");
    setFormData({
      id: feeType.id,
      fee_type_name: feeType.fee_type_name,
      description: feeType.description || "",
      status_id:
        feeType.status_id != null && feeType.status_id !== ""
          ? Number(feeType.status_id)
          : 1,
    });
    setIsEditing(true);
    scrollToMasterForm();
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await deleteFeeType(itemToDelete.id, csrfToken);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      setSuccessMsg("Fee type removed from the directory.");
      await loadFeeTypes();
      onDirectoryChange?.();
    } catch (err) {
      alert("Failed to remove: " + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      id: null,
      fee_type_name: "",
      description: "",
      status_id: 1,
    });
    setFormError(null);
    setFormErrorDetails({});
    setIsEditing(false);
  };

  if (loading) return <p className="ub-loading">Loading Fee Types…</p>;
  if (error) return <p className="ub-error">Error: {error}</p>;

  const dataList = Array.isArray(feeTypes) ? feeTypes : feeTypes.data || [];
  const ALERT_ID = "ms-fee-master-alert";

  return (
    <div className="ub-master-section">
      <GlobalSuccessFlashModal
        message={successMsg}
        onDismiss={() => setSuccessMsg("")}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.25rem",
        }}
      >
        <h3 className="ub-section__subhead" style={{ margin: 0 }}>
          Fee Type Master
        </h3>
        {/* <GlobalButton
          variant="primary"
          size="sm"
          onClick={() => {
            setSuccessMsg("");
            setFormData({
              id: null,
              fee_type_name: "",
              description: "",
              status_id: 1,
            });
            setIsEditing(false);
            setFormError(null);
            setFormErrorDetails({});
            scrollToMasterForm();
          }}
        >
          Add New Fee Type
        </GlobalButton> */}
      </div>

      <div ref={formSectionRef} style={{ scrollMarginTop: "1rem" }}>
        <GlobalFormShell
          legend={isEditing ? "Edit fee type" : "Add fee type"}
          showAlert={!!formError}
          alertText={formError}
          alertId={ALERT_ID}
          formAlertRef={alertRef}
          actions={
            <>
              {isEditing ? (
                <GlobalButton variant="ghost" onClick={handleCancel}>
                  Cancel edit
                </GlobalButton>
              ) : null}
              <GlobalButton variant="primary" onClick={handleSubmit}>
                {isEditing ? "Save fee type" : "Create fee type"}
              </GlobalButton>
            </>
          }
        >
          <GlobalTextField
            id={isEditing ? "fee_type_name_edit" : "fee_type_name"}
            label="Fee Type Name"
            value={formData.fee_type_name}
            onChange={(v) => setFormField("fee_type_name", v)}
            error={formErrorDetails.fee_type_name}
            baseAlertId={ALERT_ID}
            required
          />
          <GlobalTextField
            id={isEditing ? "description_edit" : "description"}
            label="Description"
            value={formData.description}
            onChange={(v) => setFormField("description", v)}
            error={formErrorDetails.description}
            baseAlertId={ALERT_ID}
          />
          {isEditing ? (
            <GlobalSelectField
              id="fee_type_status_id"
              label="Status"
              value={String(formData.status_id)}
              onChange={(v) => setFormField("status_id", Number(v))}
              options={STATUS_OPTIONS}
              error={formErrorDetails.status_id}
              baseAlertId={ALERT_ID}
            />
          ) : null}
        </GlobalFormShell>
      </div>

      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Fee type master table"
        headers={[
          { key: "name", label: "Fee Type Name" },
          { key: "desc", label: "Description" },
          { key: "status", label: "Status" },
          { key: "created", label: "Created Date" },
          {
            key: "actions",
            label: "Actions",
            className: "ub-table__col-actions",
          },
        ]}
      >
        {dataList.map((feeType) => (
          <tr key={feeType.id}>
            <td style={{ fontWeight: "500" }}>{feeType.fee_type_name}</td>
            <td>{feeType.description || "—"}</td>
            <td>
              <GlobalBadge active={Number(feeType.status_id) === 1} />
            </td>
            <td>
              {feeType.created_at
                ? new Date(feeType.created_at).toLocaleDateString()
                : "—"}
            </td>
            <td className="ub-table__col-actions">
              <div className="ub-table-actions">{canEdit ? (<GlobalTableActionButton action="edit" onClick={() => handleEdit(feeType)} />) : null}{canDelete ? (<GlobalTableActionButton action="delete" onClick={() => handleDeleteClick(feeType)} />) : null}</div>
            </td>
          </tr>
        ))}
        {dataList.length === 0 && (
          <tr>
            <td
              colSpan={5}
              style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}
            >
              No Fee Types Found
            </td>
          </tr>
        )}
      </GlobalDataTable>

      <GlobalModal
        open={showDeleteConfirm}
        onClose={() => !isDeleting && setShowDeleteConfirm(false)}
        title="Confirm removal"
        footer={
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
            }}
          >
            <GlobalButton
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              variant="danger"
              onClick={handleConfirmDelete}
              loading={isDeleting}
            >
              Remove
            </GlobalButton>
          </div>
        }
      >
        <p>
          Remove fee type <b>{itemToDelete?.fee_type_name}</b> from the
          directory? It will be hidden from lists (marked inactive).
        </p>
      </GlobalModal>
    </div>
  );
};

export default FeeTypeMaster;
